<section class="configurator-shell">
  @if (showTabHeaders()) {
    <div class="configurator-tabs" role="tablist">
      @for (tabId of visibleTabs(); track tabId) {
        <button
          type="button"
          class="configurator-tab"
          [class.configurator-tab-active]="activeTab() === tabId"
          role="tab"
          [attr.aria-selected]="activeTab() === tabId"
          (click)="selectTab(tabId)"
        >
          {{ getTabLabel(tabId) }}
        </button>
      }
    </div>
  }

  @if (layoutConfig().showGates && (!showTabHeaders() || activeTab() === 'gates')) {
    <section class="configurator-section">
      <div class="configurator-section-heading">
        @if (!showTabHeaders()) {
          <h3 class="configurator-section-title">{{ getTabLabel('gates') }}</h3>
        }

        <div class="configurator-sort-control" role="group" [attr.aria-label]="t('humanDesign.chart.configurator.sortGroupAria')">
          <i class="fas fa-sort-amount-down configurator-sort-icon" aria-hidden="true"></i>

          <div class="switch primary-switch configurator-sort-switch">
            <label class="mb-0">
              <span class="configurator-sort-label">{{ t('humanDesign.chart.configurator.sortGatesAscending') }}</span>
              <input type="checkbox" [checked]="sortGatesAscending()" (change)="onGateSortModeChange($event)" />
              <span class="lever"></span>
            </label>
          </div>
        </div>

        <button
          type="button"
          class="configurator-reset-button configurator-view-toggle"
          data-test-id="configurator-gate-keynotes-toggle"
          [class.configurator-view-toggle-active]="gateKeynoteView()"
          [attr.aria-pressed]="gateKeynoteView()"
          [title]="t(gateKeynoteView() ? 'humanDesign.chart.configurator.gateCompactView' : 'humanDesign.chart.configurator.gateKeynotesView')"
          [attr.aria-label]="t(gateKeynoteView() ? 'humanDesign.chart.configurator.gateCompactView' : 'humanDesign.chart.configurator.gateKeynotesView')"
          (click)="toggleGateKeynoteView()"
        >
          <i [class]="'fas fa-fw ' + (gateKeynoteView() ? 'fa-grip' : 'fa-list-ul')" aria-hidden="true"></i>
        </button>
      </div>

      <div class="center-grid">
        @for (center of centerViewModels(); track center.center) {
          <article class="center-card">
            <div class="center-card-header">
              <strong>{{ center.centerLabel }}</strong>
              <span
                class="center-badge"
                [class.center-badge-defined]="center.definitionState === 'defined'"
                [style.background-color]="center.backgroundColor"
                [style.color]="center.textColor"
              >
                {{ getCenterDefinitionLabel(center.definitionState) }}
              </span>
            </div>

            <div class="control-grid control-grid-gates" [class.control-grid-gates-keynotes]="gateKeynoteView()">
              @for (gate of center.gates; track gate.gate) {
                <button
                  class="control-button"
                  [class.control-button-gate-keynotes]="gateKeynoteView()"
                  [class.control-button-used]="gate.colorState !== 'inactive'"
                  type="button"
                  [attr.aria-label]="getGateAriaLabel(gate.gate, gate.colorState)"
                  (click)="cycleGate(gate.gate)"
                >
                  @if (gateKeynoteView()) {
                    <span class="gate-keynote-row">
                      <span class="control-key">{{ gate.gate }}</span>
                      <span [class]="'state-pill ' + getStateClass(gate.colorState)">
                        {{ getVisibleStateLabel(gate.colorState) }}
                      </span>
                    </span>
                    @if (gate.keynote1 || gate.keynote2) {
                      <span class="gate-keynotes">
                        @if (gate.keynote1) {
                          <span class="gate-keynote-line">{{ gate.keynote1 }}</span>
                        }
                        @if (gate.keynote2) {
                          <span class="gate-keynote-line">{{ gate.keynote2 }}</span>
                        }
                      </span>
                    }
                  } @else {
                    <span class="control-key">{{ gate.gate }}</span>
                    <span [class]="'state-pill ' + getStateClass(gate.colorState)">
                      {{ getVisibleStateLabel(gate.colorState) }}
                    </span>
                  }
                </button>
              }
            </div>
          </article>
        }
      </div>
    </section>
  }

  @if (layoutConfig().showCenters && (!showTabHeaders() || activeTab() === 'centers')) {
    <section class="configurator-section">
      <div class="configurator-section-heading configurator-circuit-freeze-row">
        <div
          class="switch primary-switch configurator-freeze-switch"
          [class.is-disabled]="circuitFreezeModeDisabled()"
          [title]="circuitFreezeModeDisabled() ? t(circuitFreezeModeDisabledReasonKey()) : ''"
        >
          <label class="mb-0">
            <span class="configurator-freeze-label">{{ t('humanDesign.chart.circuitConfigurator.freezeMode.label') }}</span>
            <input
              type="checkbox"
              [checked]="circuitFreezeMode()"
              [disabled]="circuitFreezeModeDisabled()"
              [attr.aria-pressed]="circuitFreezeMode()"
              (change)="onCircuitFreezeModeChange($event)"
            />
            <span class="lever"></span>
          </label>
        </div>

        <button
          type="button"
          class="configurator-reset-button"
          [disabled]="!hasAnyActiveCenter()"
          (click)="resetAllCenters()"
        >
          <i class="fas fa-undo" aria-hidden="true"></i>
          <span>{{ t('humanDesign.chart.configurator.resetAllCenters') }}</span>
        </button>
      </div>

      <div class="control-grid control-grid-circuits">
        @for (center of centerToggleViewModels(); track center.center) {
          <div
            class="control-button control-button-circuit control-button-stream control-button-center"
            [class.control-button-used]="center.colorState !== 'inactive'"
            role="button"
            tabindex="0"
            [attr.aria-label]="getCenterAriaLabel(center.label, center.colorState)"
            (click)="cycleCenter(center.center)"
            (keydown.enter)="onCenterKeydown($event, center.center)"
            (keydown.space)="onCenterKeydown($event, center.center)"
          >
            <span class="stream-color-dot" [style.background-color]="center.color" aria-hidden="true"></span>

            <span class="circuit-name-block">
              <span class="stream-title-row">
                <span class="control-key circuit-name">{{ center.label }}</span>
              </span>
            </span>

            <span [class]="'state-pill ' + getStateClass(center.colorState)">
              {{ getVisibleStateLabel(center.colorState) }}
            </span>
          </div>
        }
      </div>
    </section>
  }

  @if (layoutConfig().showMisc && (!showTabHeaders() || activeTab() === 'misc')) {
    <section class="configurator-section">
      @if (!showTabHeaders()) {
        <h3 class="configurator-section-title">{{ getTabLabel('misc') }}</h3>
      }

      <!-- Poort-eigenschappen: één pil per eigenschap (druk, liefde, geheugen, …); de hele
           poortset cyclet of bevriest in één klik, zoals een center. -->
      <h4 class="configurator-subsection-title">{{ t('humanDesign.chart.configurator.misc.sectionGateAttributes') }}</h4>

      <div class="configurator-section-heading configurator-circuit-freeze-row configurator-gate-attributes-heading">
        <div
          class="switch primary-switch configurator-freeze-switch"
          [class.is-disabled]="circuitFreezeModeDisabled()"
          [title]="circuitFreezeModeDisabled() ? t(circuitFreezeModeDisabledReasonKey()) : ''"
        >
          <label class="mb-0">
            <span class="configurator-freeze-label">{{ t('humanDesign.chart.circuitConfigurator.freezeMode.label') }}</span>
            <input
              type="checkbox"
              [checked]="circuitFreezeMode()"
              [disabled]="circuitFreezeModeDisabled()"
              [attr.aria-pressed]="circuitFreezeMode()"
              (change)="onCircuitFreezeModeChange($event)"
            />
            <span class="lever"></span>
          </label>
        </div>

        <button
          type="button"
          class="configurator-reset-button"
          data-test-id="configurator-reset-gate-attributes"
          [disabled]="!hasAnyActiveGateAttribute()"
          (click)="resetAllGateAttributes()"
        >
          <i class="fas fa-undo" aria-hidden="true"></i>
          <span>{{ t('humanDesign.chart.configurator.resetAllGateAttributes') }}</span>
        </button>
      </div>

      <div class="control-grid control-grid-circuits control-grid-gate-attributes">
        @for (attribute of gateAttributeViewModels(); track attribute.key) {
          <div
            class="control-button control-button-circuit control-button-gate-attribute"
            [class.control-button-used]="attribute.colorState !== 'inactive'"
            role="button"
            tabindex="0"
            [attr.data-attribute-key]="attribute.key"
            [attr.aria-label]="getGateAttributeAriaLabel(attribute.label, attribute.colorState)"
            (click)="cycleGateAttribute(attribute.key)"
            (keydown.enter)="onGateAttributeKeydown($event, attribute.key)"
            (keydown.space)="onGateAttributeKeydown($event, attribute.key)"
          >
            <i [class]="'fas fa-' + attribute.icon + ' circuit-icon'" aria-hidden="true"></i>

            <span class="circuit-name-block">
              <span class="control-key circuit-name">{{ attribute.label }}</span>
              <span class="circuit-subtitle-row">
                <span class="circuit-subtitle gate-attribute-gates">{{ attribute.gateList }}</span>
              </span>
            </span>

            <span [class]="'state-pill ' + getStateClass(attribute.colorState)">
              {{ getVisibleStateLabel(attribute.colorState) }}
            </span>
          </div>
        }
      </div>

      <p class="configurator-mandala-hint">{{ t('humanDesign.chart.configurator.misc.gateAttributesHint') }}</p>

      <h4 class="configurator-subsection-title">{{ t('humanDesign.chart.configurator.misc.sectionMandala') }}</h4>

      <div class="configurator-mandala-options">
        <div class="switch primary-switch configurator-mandala-switch">
          <label class="mb-0">
            <span class="configurator-mandala-label">
              <span class="configurator-mandala-swatch configurator-mandala-swatch-channel" aria-hidden="true"></span>
              {{ t('humanDesign.chart.configurator.mandala.channelLinks') }}
            </span>
            <input
              type="checkbox"
              [checked]="mandalaPatternOptions().showChannelLinks"
              (change)="onMandalaPatternOptionChange('showChannelLinks', $event)"
            />
            <span class="lever"></span>
          </label>
        </div>

        <div class="switch primary-switch configurator-mandala-switch">
          <label class="mb-0">
            <span class="configurator-mandala-label">
              <span class="configurator-mandala-swatch configurator-mandala-swatch-partner" aria-hidden="true"></span>
              {{ t('humanDesign.chart.configurator.mandala.partnerLinks') }}
            </span>
            <input
              type="checkbox"
              [checked]="mandalaPatternOptions().showPartnerLinks"
              (change)="onMandalaPatternOptionChange('showPartnerLinks', $event)"
            />
            <span class="lever"></span>
          </label>
        </div>

        <div class="switch primary-switch configurator-mandala-switch">
          <label class="mb-0">
            <span class="configurator-mandala-label">
              <span class="configurator-mandala-swatch configurator-mandala-swatch-cross" aria-hidden="true"></span>
              {{ t('humanDesign.chart.configurator.mandala.incarnationCross') }}
            </span>
            <input
              type="checkbox"
              [checked]="mandalaPatternOptions().showIncarnationCross"
              (change)="onMandalaPatternOptionChange('showIncarnationCross', $event)"
            />
            <span class="lever"></span>
          </label>
        </div>

        <div class="switch primary-switch configurator-mandala-switch">
          <label class="mb-0">
            <span class="configurator-mandala-label">
              <span class="configurator-mandala-swatch configurator-mandala-swatch-life-cross" aria-hidden="true"></span>
              {{ t('humanDesign.chart.configurator.mandala.lifeCross') }}
            </span>
            <input
              type="checkbox"
              [checked]="mandalaPatternOptions().showLifeCross"
              (change)="onMandalaPatternOptionChange('showLifeCross', $event)"
            />
            <span class="lever"></span>
          </label>
        </div>
      </div>

      <p class="configurator-mandala-hint">{{ t('humanDesign.chart.configurator.mandala.hint') }}</p>

      <h4 class="configurator-subsection-title">{{ t('humanDesign.chart.configurator.misc.sectionBodygraph') }}</h4>

      <div class="configurator-mandala-options">
        <div class="switch primary-switch configurator-mandala-switch configurator-monopole-switch">
          <label class="mb-0">
            <span class="configurator-mandala-label">
              <span class="configurator-mandala-swatch configurator-mandala-swatch-monopole" aria-hidden="true"></span>
              {{ t('humanDesign.chart.configurator.misc.monopole') }}
            </span>
            <input
              type="checkbox"
              [checked]="showMonopoleAndCrystals()"
              (change)="onMonopoleAndCrystalsChange($event)"
            />
            <span class="lever"></span>
          </label>
        </div>
      </div>
    </section>
  }

  @if (layoutConfig().showChannels && (!showTabHeaders() || activeTab() === 'channels')) {
    <section class="configurator-section">
      @if (!showTabHeaders()) {
        <h3 class="configurator-section-title">{{ getTabLabel('channels') }}</h3>
      }

      <div class="configurator-channel-actions">
        <button
          type="button"
          class="configurator-reset-button"
          (click)="fillAllRequested.emit()"
        >
          <i class="fas fa-fill-drip" aria-hidden="true"></i>
          <span>{{ t('humanDesign.chart.configurator.fillAll') }}</span>
        </button>

        <button
          type="button"
          class="configurator-reset-button"
          (click)="clearAllRequested.emit()"
        >
          <i class="fas fa-eraser" aria-hidden="true"></i>
          <span>{{ t('humanDesign.chart.configurator.clearAll') }}</span>
        </button>

        <button
          type="button"
          class="configurator-reset-button configurator-view-toggle"
          data-test-id="configurator-channel-keynotes-toggle"
          [class.configurator-view-toggle-active]="channelKeynoteView()"
          [attr.aria-pressed]="channelKeynoteView()"
          [title]="t(channelKeynoteView() ? 'humanDesign.chart.configurator.channelCompactView' : 'humanDesign.chart.configurator.channelKeynotesView')"
          [attr.aria-label]="t(channelKeynoteView() ? 'humanDesign.chart.configurator.channelCompactView' : 'humanDesign.chart.configurator.channelKeynotesView')"
          (click)="toggleChannelKeynoteView()"
        >
          <i [class]="'fas fa-fw ' + (channelKeynoteView() ? 'fa-grip' : 'fa-list-ul')" aria-hidden="true"></i>
        </button>
      </div>

      @let keynoteLabels = channelKeynoteLabels();
      <div class="control-grid control-grid-channels" [class.control-grid-channels-keynotes]="channelKeynoteView()">
        @for (channel of channelViewModels(); track channel.key) {
          <div
            class="control-button control-button-channel"
            [class.control-button-used]="channel.colorState !== 'inactive'"
            role="button"
            tabindex="0"
            [attr.aria-label]="getChannelAriaLabel(channel.key, channel.colorState, channel.isDefined)"
            (click)="cycleChannel(channel.key)"
            (keydown.enter)="onChannelKeydown($event, channel.key)"
            (keydown.space)="onChannelKeydown($event, channel.key)"
          >
            <span class="channel-row channel-row-top">
              <span class="channel-title-row">
                <span class="control-key">{{ channel.key }}</span>
                <button
                  type="button"
                  class="channel-info-icon-btn"
                  [title]="t('humanDesign.chart.configurator.openChannelInfo')"
                  [attr.aria-label]="t('humanDesign.chart.configurator.openChannelInfo')"
                  (click)="openChannelInfo(channel.key, $event)"
                >
                  <i class="fas fa-circle-info" aria-hidden="true"></i>
                </button>
              </span>
              <span [class]="'state-pill ' + getStateClass(channel.colorState)">
                {{ getVisibleStateLabel(channel.colorState) }}
              </span>
            </span>
            @if (channelKeynoteView() && (channel.name || channel.shortDescription)) {
              <span class="channel-keynotes">
                @if (channel.name) {
                  <span class="channel-keynote-line">{{ keynoteLabels.name }}<strong>{{ channel.name }}</strong></span>
                }
                @if (channel.shortDescription) {
                  <span class="channel-keynote-line">{{ keynoteLabels.description }}{{ channel.shortDescription }}</span>
                }
              </span>
            }
          </div>
        }
      </div>
    </section>
  }

  @if (layoutConfig().showCircuits && (!showTabHeaders() || activeTab() === 'circuits')) {
    <section class="configurator-section">
      <div class="configurator-section-heading configurator-circuit-freeze-row">
        <div
          class="switch primary-switch configurator-freeze-switch"
          [class.is-disabled]="circuitFreezeModeDisabled()"
          [title]="circuitFreezeModeDisabled() ? t(circuitFreezeModeDisabledReasonKey()) : ''"
        >
          <label class="mb-0">
            <span class="configurator-freeze-label">{{ t('humanDesign.chart.circuitConfigurator.freezeMode.label') }}</span>
            <input
              type="checkbox"
              [checked]="circuitFreezeMode()"
              [disabled]="circuitFreezeModeDisabled()"
              [attr.aria-pressed]="circuitFreezeMode()"
              (change)="onCircuitFreezeModeChange($event)"
            />
            <span class="lever"></span>
          </label>
        </div>

        <!-- Rechts: reset + het algemene circuit-info-icoon (vierkant, enkel icoon). -->
        <span class="configurator-circuit-actions">
          <button
            type="button"
            class="configurator-reset-button"
            [disabled]="!hasAnyActiveCircuit()"
            (click)="resetAllCircuits()"
          >
            <i class="fas fa-undo" aria-hidden="true"></i>
            <span>{{ t('humanDesign.chart.configurator.resetAllCircuits') }}</span>
          </button>
          <button
            type="button"
            class="configurator-info-button"
            [title]="circuitInfoUiLabels().openOverviewInfo"
            [attr.aria-label]="circuitInfoUiLabels().openOverviewInfo"
            (click)="openOverviewInfo()"
          >
            <i class="fas fa-circle-info" aria-hidden="true"></i>
          </button>
        </span>
      </div>

      @for (familyGroup of circuitFamilyViewModels(); track familyGroup.family) {
        <div class="circuit-family-group">
          <h4 class="circuit-family-heading">{{ familyGroup.label }}</h4>

          <div class="control-grid control-grid-circuits">
            @for (circuit of familyGroup.circuits; track circuit.key) {
              <div class="circuit-button-wrap">
                <div
                  class="control-button control-button-circuit"
                  [class.control-button-used]="circuit.colorState !== 'inactive'"
                  role="button"
                  tabindex="0"
                  [attr.aria-label]="getCircuitAriaLabel(circuit.name, circuit.colorState)"
                  (click)="cycleCircuit(circuit.key)"
                  (keydown.enter)="onCircuitKeydown($event, circuit.key)"
                  (keydown.space)="onCircuitKeydown($event, circuit.key)"
                >
                  @if (circuit.themeIcon) {
                    <i
                      [class]="'fas fa-' + circuit.themeIcon + ' circuit-icon'"
                      [title]="circuit.themeLabel ?? ''"
                      [attr.aria-label]="circuit.themeLabel ?? null"
                    ></i>
                  }

                  <span class="circuit-name-block">
                    <span class="control-key circuit-name">{{ circuit.name }}</span>
                    <span class="circuit-subtitle-row">
                      @if (circuit.themeLabel) {
                        <span class="circuit-subtitle">{{ circuit.themeLabel }}</span>
                      }
                      <button
                        type="button"
                        class="circuit-info-icon-btn"
                        [title]="circuitInfoUiLabels().openCircuitInfo"
                        [attr.aria-label]="circuitInfoUiLabels().openCircuitInfo"
                        (click)="openCircuitInfo(circuit.key, $event)"
                      >
                        <i class="fas fa-circle-info" aria-hidden="true"></i>
                      </button>
                    </span>
                  </span>

                  <span [class]="'state-pill ' + getStateClass(circuit.colorState)">
                    {{ getVisibleStateLabel(circuit.colorState) }}
                  </span>
                </div>
              </div>
            }
          </div>
        </div>
      }

      <!-- Geheugencircuit: geen hoofd-/subcircuit maar een volledig apart
           circuit met eigen betekenis, daarom een eigen sectie onderaan. Het
           info-icoon (en de hele pil) openen het memory-circuit-paneel. -->
      <div class="circuit-family-group circuit-family-group--memory">
        <h4 class="circuit-family-heading">{{ memoryCircuit().label }}</h4>

        <div class="control-grid control-grid-circuits">
          <div class="circuit-button-wrap">
            <div
              class="control-button control-button-circuit control-button-memory"
              role="button"
              tabindex="0"
              [attr.aria-label]="memoryCircuit().label"
              (click)="openMemoryInfo($event)"
              (keydown.enter)="openMemoryInfo($event)"
              (keydown.space)="openMemoryInfo($event)"
            >
              <i class="fas fa-brain circuit-icon" aria-hidden="true"></i>

              <span class="circuit-name-block">
                <span class="control-key circuit-name">{{ memoryCircuit().label }}</span>
                <span class="circuit-subtitle-row">
                  @if (memoryCircuit().subtitle) {
                    <span class="circuit-subtitle">{{ memoryCircuit().subtitle }}</span>
                  }
                  <button
                    type="button"
                    class="circuit-info-icon-btn"
                    [title]="circuitInfoUiLabels().openCircuitInfo"
                    [attr.aria-label]="circuitInfoUiLabels().openCircuitInfo"
                    (click)="openMemoryInfo($event)"
                  >
                    <i class="fas fa-circle-info" aria-hidden="true"></i>
                  </button>
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Mystiek circuit: een aparte stroom over acht poorten (19, 49, 37, 40,
           51, 25, 10, 20), daarom zoals het geheugen een eigen sectie. Wel een
           volwaardige circuit-pil: klik cyclet of bevriest de poorten, het
           info-icoon opent het mystiek-paneel. -->
      <div class="circuit-family-group circuit-family-group--memory circuit-family-group--mystical">
        <h4 class="circuit-family-heading">{{ mysticalCircuit().label }}</h4>

        <div class="control-grid control-grid-circuits">
          <div class="circuit-button-wrap">
            <div
              class="control-button control-button-circuit control-button-mystical"
              [class.control-button-used]="mysticalCircuit().colorState !== 'inactive'"
              role="button"
              tabindex="0"
              data-test-id="configurator-mystical-circuit"
              [attr.aria-label]="getCircuitAriaLabel(mysticalCircuit().label, mysticalCircuit().colorState)"
              (click)="cycleMysticalCircuit()"
              (keydown.enter)="onMysticalCircuitKeydown($event)"
              (keydown.space)="onMysticalCircuitKeydown($event)"
            >
              <i [class]="'fas fa-' + mysticalIcon + ' circuit-icon'" aria-hidden="true"></i>

              <span class="circuit-name-block">
                <span class="control-key circuit-name">{{ mysticalCircuit().label }}</span>
                <span class="circuit-subtitle-row">
                  @if (mysticalCircuit().subtitle) {
                    <span class="circuit-subtitle">{{ mysticalCircuit().subtitle }}</span>
                  }
                  <button
                    type="button"
                    class="circuit-info-icon-btn"
                    data-test-id="configurator-mystical-info"
                    [title]="circuitInfoUiLabels().openCircuitInfo"
                    [attr.aria-label]="circuitInfoUiLabels().openCircuitInfo"
                    (click)="openMysticalInfo($event)"
                  >
                    <i class="fas fa-circle-info" aria-hidden="true"></i>
                  </button>
                </span>
              </span>

              <span [class]="'state-pill ' + getStateClass(mysticalCircuit().colorState)">
                {{ getVisibleStateLabel(mysticalCircuit().colorState) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  }

  @if (layoutConfig().showStreams && (!showTabHeaders() || activeTab() === 'streams')) {
    <section class="configurator-section">
      <div class="configurator-section-heading configurator-circuit-freeze-row">
        <div
          class="switch primary-switch configurator-freeze-switch"
          [class.is-disabled]="circuitFreezeModeDisabled()"
          [title]="circuitFreezeModeDisabled() ? t(circuitFreezeModeDisabledReasonKey()) : ''"
        >
          <label class="mb-0">
            <span class="configurator-freeze-label">{{ t('humanDesign.chart.circuitConfigurator.freezeMode.label') }}</span>
            <input
              type="checkbox"
              [checked]="circuitFreezeMode()"
              [disabled]="circuitFreezeModeDisabled()"
              [attr.aria-pressed]="circuitFreezeMode()"
              (change)="onCircuitFreezeModeChange($event)"
            />
            <span class="lever"></span>
          </label>
        </div>

        <button
          type="button"
          class="configurator-reset-button"
          [disabled]="!hasAnyActiveStream()"
          (click)="resetAllStreams()"
        >
          <i class="fas fa-undo" aria-hidden="true"></i>
          <span>{{ t('humanDesign.chart.configurator.resetAllStreams') }}</span>
        </button>
      </div>

      @for (group of streamAwarenessGroupViewModels(); track group.awareness) {
        <div class="circuit-family-group">
          <h4 class="circuit-family-heading">
            {{ group.label }}
            @if (group.subtitle) {
              <span class="stream-awareness-subtitle">{{ group.subtitle }}</span>
            }
          </h4>

          <div class="control-grid control-grid-circuits">
            @for (stream of group.streams; track stream.key) {
              <div
                class="control-button control-button-circuit control-button-stream"
                [class.control-button-used]="stream.colorState !== 'inactive'"
                role="button"
                tabindex="0"
                [attr.aria-label]="getStreamAriaLabel(stream.name, stream.colorState)"
                (click)="cycleStream(stream.key)"
                (keydown.enter)="onStreamKeydown($event, stream.key)"
                (keydown.space)="onStreamKeydown($event, stream.key)"
              >
                <span class="stream-color-dot" [style.background-color]="stream.color" aria-hidden="true"></span>

                <span class="circuit-name-block">
                  <span class="stream-title-row">
                    <span class="control-key circuit-name">{{ stream.name }}</span>
                    <button
                      type="button"
                      class="circuit-info-icon-btn"
                      [title]="t('humanDesign.chart.configurator.openStreamInfo')"
                      [attr.aria-label]="t('humanDesign.chart.configurator.openStreamInfo')"
                      (click)="openStreamInfo(stream.key, $event)"
                    >
                      <i class="fas fa-circle-info" aria-hidden="true"></i>
                    </button>
                  </span>
                </span>

                <span [class]="'state-pill ' + getStateClass(stream.colorState)">
                  {{ getVisibleStateLabel(stream.colorState) }}
                </span>
              </div>
            }
          </div>
        </div>
      }
    </section>
  }
</section>
